import { Link } from "react-router-dom";
import { ArrowRight, Play, Sparkles, Headphones, Radio, Gamepad2, Video, Coins, Users, ShieldCheck, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/site/Reveal";
import SectionHeading from "@/components/site/SectionHeading";

const FEATURED = [
  { tone: "am-tile-pink",     kicker: "New Music", title: "Discover tracks worth mining.", copy: "LiveLink identifies music anywhere it plays — and unlocks RPM you can actually keep." },
  { tone: "am-tile-peach",    kicker: "For Artists", title: "Turn attention into income.",  copy: "Embed rewards in your catalog. Your listeners become your promoters." },
  { tone: "am-tile-mint",     kicker: "For Fans",    title: "Your ears just got a paycheck.", copy: "Every play, every lyric game, every deep listen — rewarded." },
  { tone: "am-tile-lavender", kicker: "Community",   title: "A music universe you shape.",  copy: "Real fans and real artists building real value together." },
];

const PILLARS = [
  { icon: Headphones, tone: "am-tile-mint",    label: "Listen",   copy: "Full-fidelity D1 Audio — music the way it was mixed." },
  { icon: Radio,      tone: "am-tile-peach",   label: "Discover", copy: "Aim, tap, identify. LiveLink finds it in seconds." },
  { icon: Gamepad2,   tone: "am-tile-lemon",   label: "Play",     copy: "Lyric-bubble games score the highest RPM rewards." },
  { icon: Video,      tone: "am-tile-lavender",label: "Watch",    copy: "Videos, live lyrics, photo slideshows — all in beat." },
];

export default function Home() {
  return (
    <main data-testid="page-home" className="pt-24 md:pt-32">
      {/* ================= HERO ================= */}
      <section className="relative">
        <div className="container relative">
          <div className="am-blob" style={{ right: "-140px", top: "-80px" }} />
          <div className="am-blob am-blob-orange" style={{ left: "-140px", top: "60px" }} />

          <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 flex flex-col gap-8">
              <Reveal>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.24em] text-primary font-semibold border border-black/5 self-start">
                  <Sparkles className="h-3.5 w-3.5" /> The music you love, rewarded
                </span>
              </Reveal>

              <Reveal delay={80}>
                <h1 className="font-display font-bold tracking-tight text-[clamp(2.75rem,7.5vw,6.5rem)] leading-[0.95]">
                  <span className="block">Music is meant</span>
                  <span className="block">to <span className="am-sunset-text">move you.</span></span>
                  <span className="block font-serif-editorial italic font-normal text-foreground/85 mt-2">
                    Now it pays you back.
                  </span>
                </h1>
              </Reveal>

              <Reveal delay={140}>
                <p className="text-lg md:text-xl text-foreground/65 max-w-xl leading-relaxed">
                  AudioMine turns every listen, every lyric, every discovery into <b className="text-foreground">RPM rewards</b>.
                  Full-fidelity sound, live gamification, and a community that pays fans and artists to show up.
                </p>
              </Reveal>

              <Reveal delay={220}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/for-artists">
                    <Button
                      data-testid="hero-cta-artist"
                      size="lg"
                      className="rounded-full h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base shadow-[0_10px_30px_-8px_rgba(250,37,104,0.55)] hover:shadow-[0_16px_40px_-6px_rgba(250,37,104,0.75)] transition-shadow"
                    >
                      I'm an Artist <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/for-fans">
                    <Button
                      data-testid="hero-cta-fan"
                      size="lg"
                      variant="outline"
                      className="rounded-full h-14 px-8 border-black/10 bg-white hover:bg-white/70 text-foreground font-semibold text-base shadow-sm"
                    >
                      I'm a Fan <Play className="ml-2 h-4 w-4 fill-foreground" />
                    </Button>
                  </Link>
                </div>
              </Reveal>

              <Reveal delay={320}>
                <div className="flex items-center gap-6 pt-2">
                  <div className="flex -space-x-2">
                    {["am-tile-pink","am-tile-peach","am-tile-mint","am-tile-lavender"].map((t,i)=>(
                      <span key={i} className={`h-9 w-9 rounded-full ${t} ring-4 ring-background`} />
                    ))}
                  </div>
                  <div>
                    <div className="font-display font-bold text-lg leading-none">207M+</div>
                    <div className="text-xs text-foreground/55 mt-1">RPM tokens mined worldwide</div>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Right visual — stacked "album cards" */}
            <div className="lg:col-span-5 relative">
              <Reveal delay={220}>
                <div className="relative h-[520px] md:h-[560px]">
                  {/* Card 1 — pink */}
                  <div className="absolute right-4 top-0 w-64 md:w-72 aspect-[3/4] rounded-[28px] overflow-hidden am-tile-pink shadow-[0_30px_60px_-20px_rgba(250,37,104,0.4)] rotate-[6deg] hover:rotate-[3deg] transition-transform duration-500">
                    <img alt="" src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?auto=format&fit=crop&w=800&q=80" className="h-full w-full object-cover mix-blend-luminosity opacity-90" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-5 right-5 text-white">
                      <div className="text-[10px] uppercase tracking-widest opacity-80">Now Mining</div>
                      <div className="font-display font-bold text-xl mt-1">Midnight Loop</div>
                      <div className="text-xs opacity-80 mt-0.5">Neon Static — Alt Wave</div>
                    </div>
                  </div>

                  {/* Card 2 — mint */}
                  <div className="absolute left-0 top-20 w-56 md:w-60 aspect-[3/4] rounded-[28px] overflow-hidden am-tile-mint shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)] -rotate-[8deg] hover:-rotate-[3deg] transition-transform duration-500">
                    <img alt="" src="https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=800&q=80" className="h-full w-full object-cover mix-blend-luminosity opacity-80" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-5 right-5 text-white">
                      <div className="text-[10px] uppercase tracking-widest opacity-80">Featured</div>
                      <div className="font-display font-bold text-lg mt-1">Solar Bloom</div>
                      <div className="text-xs opacity-80 mt-0.5">Kai River — Indie Pop</div>
                    </div>
                  </div>

                  {/* Card 3 — lavender floating small */}
                  <div className="absolute right-8 bottom-6 w-52 md:w-56 aspect-square rounded-[24px] overflow-hidden am-tile-lavender shadow-[0_20px_50px_-16px_rgba(185,157,255,0.6)] rotate-[3deg]">
                    <img alt="" src="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=600&q=80" className="h-full w-full object-cover mix-blend-luminosity opacity-90" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <div className="text-[10px] uppercase tracking-widest opacity-80">Editor's Pick</div>
                      <div className="font-display font-bold text-base mt-1">Coastal Hymns</div>
                    </div>
                  </div>

                  {/* Floating play button */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-20 w-20 rounded-full bg-white shadow-[0_20px_50px_-6px_rgba(0,0,0,0.3)] flex items-center justify-center hover:scale-105 transition-transform">
                    <Play className="h-8 w-8 text-primary fill-primary ml-1" />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="mt-16 border-y border-black/5 py-6 overflow-hidden bg-white/50 backdrop-blur">
          <div className="flex am-marquee gap-16 whitespace-nowrap">
            {Array.from({ length: 2 }).map((_, dup) => (
              <div key={dup} className="flex items-center gap-16 shrink-0">
                {["LISTEN · DISCOVER · PLAY · WATCH","RPM ENABLED","D1 AUDIO FIDELITY","AUDIO MINED BY THE WORLD","SUPPORT INDEPENDENT ARTISTS","REWARD PERFORMANCE MARKER"].map((t,i)=>(
                  <span key={`${dup}-${i}`} className="font-display font-semibold text-lg md:text-xl uppercase tracking-[0.2em] text-foreground/50">
                    <span className="text-primary mr-4">●</span>{t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FEATURED / EDITORIAL ================= */}
      <section className="relative py-24 md:py-32">
        <div className="container">
          <SectionHeading
            eyebrow="Featured"
            title={<>A universe of music, <span className="am-sunset-text">gamified.</span></>}
            subtitle="AudioMine isn't another streaming app. It's a joyful, rewarded way to experience the music you love."
          />

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {FEATURED.map((f, i) => (
              <Reveal key={f.title} delay={i * 90}>
                <div className={`relative overflow-hidden rounded-[32px] ${f.tone} p-10 md:p-12 min-h-[280px] flex flex-col justify-end group cursor-pointer`}>
                  <div className="absolute top-6 left-6 md:top-8 md:left-8">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold text-foreground/80">
                      {f.kicker}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-3xl md:text-4xl leading-tight text-foreground max-w-md">{f.title}</h3>
                  <p className="mt-3 text-foreground/70 max-w-md">{f.copy}</p>
                  <ArrowRight className="absolute right-8 bottom-8 h-6 w-6 text-foreground/70 group-hover:translate-x-1 transition-transform" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FOUR PILLARS ================= */}
      <section className="relative py-24 md:py-32 bg-secondary/60">
        <div className="container">
          <SectionHeading
            eyebrow="Four ways in"
            title="Listen. Discover. Play. Watch."
            subtitle="Four simple actions. Endless ways to mine rewards from the music around you."
            align="center"
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.label} delay={i * 90}>
                  <div className="am-soft p-8 h-full flex flex-col gap-5">
                    <div className={`h-14 w-14 rounded-2xl ${v.tone} flex items-center justify-center text-foreground shadow-inner`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="font-display font-bold text-2xl">{v.label}</div>
                    <p className="text-foreground/60 leading-relaxed">{v.copy}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= DUAL-PATH ================= */}
      <section className="relative py-24 md:py-32">
        <div className="container">
          <SectionHeading
            eyebrow="Two sides of the stage"
            title={<>Built for the ones who <span className="am-sunset-text">make</span> the music — and the ones who <span className="am-sunset-text">live</span> for it.</>}
          />

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Artists card */}
            <Reveal>
              <div className="relative overflow-hidden rounded-[36px] bg-foreground text-white p-10 md:p-14 min-h-[520px] flex flex-col justify-between">
                <img src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&w=1200&q=80" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/70 to-foreground/40" />
                <div className="relative z-10">
                  <span className="inline-flex rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold text-white">
                    For Artists
                  </span>
                </div>
                <div className="relative z-10">
                  <h3 className="font-display font-bold text-4xl md:text-5xl leading-[1.05] max-w-md">
                    Fans that actually pay attention. <span className="text-primary">Literally.</span>
                  </h3>
                  <p className="mt-5 text-white/70 max-w-md">Embed RPM tokens in your tracks. Turn every listener into an engaged supporter. Grow a community that pays for real interaction.</p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <span className="rounded-full bg-white/10 backdrop-blur px-4 py-2 text-xs font-medium">Audience growth</span>
                    <span className="rounded-full bg-white/10 backdrop-blur px-4 py-2 text-xs font-medium">Monetization</span>
                    <span className="rounded-full bg-white/10 backdrop-blur px-4 py-2 text-xs font-medium">Deep analytics</span>
                  </div>
                  <Link to="/for-artists" className="mt-8 inline-flex items-center gap-2 font-semibold text-primary group/l">
                    Open the artist toolkit <ArrowRight className="h-4 w-4 transition-transform group-hover/l:translate-x-1" />
                  </Link>
                </div>
              </div>
            </Reveal>

            {/* Fans card */}
            <Reveal delay={100}>
              <div className="relative overflow-hidden rounded-[36px] am-tile-peach p-10 md:p-14 min-h-[520px] flex flex-col justify-between">
                <img src="https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=1200&q=80" alt="" className="absolute inset-0 h-full w-full object-cover mix-blend-multiply opacity-40" />
                <div className="relative z-10">
                  <span className="inline-flex rounded-full bg-white/70 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold text-foreground">
                    For Fans
                  </span>
                </div>
                <div className="relative z-10">
                  <h3 className="font-display font-bold text-4xl md:text-5xl leading-[1.05] max-w-md text-foreground">
                    Get paid to fall in love with new music.
                  </h3>
                  <p className="mt-5 text-foreground/75 max-w-md">Mine RPM by listening deeper, playing lyric games, and championing the artists you love — then redeem for real value.</p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <span className="rounded-full bg-white/80 px-4 py-2 text-xs font-medium">Earn tokens</span>
                    <span className="rounded-full bg-white/80 px-4 py-2 text-xs font-medium">Play games</span>
                    <span className="rounded-full bg-white/80 px-4 py-2 text-xs font-medium">Redeem rewards</span>
                  </div>
                  <Link to="/for-fans" className="mt-8 inline-flex items-center gap-2 font-semibold text-primary group/l">
                    See the fan playbook <ArrowRight className="h-4 w-4 transition-transform group-hover/l:translate-x-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= INSPIRATIONAL QUOTE STRIP ================= */}
      <section className="relative py-24 md:py-32">
        <div className="container">
          <Reveal>
            <div className="mx-auto max-w-4xl text-center">
              <div className="text-[11px] uppercase tracking-[0.24em] text-primary font-semibold">Manifesto</div>
              <p className="mt-6 font-serif-editorial italic text-3xl md:text-5xl leading-[1.15] text-foreground">
                "The internet took a lot from music.
                <br /> We're giving it <span className="am-sunset-text not-italic font-display font-bold">back.</span>"
              </p>
              <div className="mt-8 flex items-center justify-center gap-3">
                <div className="h-10 w-10 rounded-full am-tile-pink" />
                <div className="text-left">
                  <div className="font-semibold text-sm">PulsBeat Media</div>
                  <div className="text-xs text-foreground/55">Creators of AudioMine</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= QUICK BENEFITS ================= */}
      <section className="relative py-24 md:py-32 bg-secondary/50">
        <div className="container grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: TrendingUp, tone: "am-tile-mint",    label: "Real growth" },
            { icon: Coins,      tone: "am-tile-peach",   label: "Real rewards" },
            { icon: Users,      tone: "am-tile-lavender",label: "Real fans" },
            { icon: ShieldCheck,tone: "am-tile-coral",   label: "Zero clout farming" },
          ].map(({ icon: Icon, tone, label }, i) => (
            <Reveal key={label} delay={i * 80}>
              <div className={`rounded-[28px] ${tone} p-6 md:p-8 aspect-square flex flex-col justify-between`}>
                <Icon className="h-7 w-7 text-foreground" />
                <div className="font-display font-bold text-xl md:text-2xl leading-tight">{label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="relative py-28 md:py-40">
        <div className="container">
          <Reveal>
            <div className="relative overflow-hidden rounded-[40px] p-12 md:p-20 text-center am-tile-pink">
              <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-white/25 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-white/25 blur-3xl" />
              <div className="relative z-10 flex flex-col items-center gap-8">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold text-primary">
                  <Sparkles className="h-3.5 w-3.5" /> Join the metaverse
                </span>
                <h2 className="font-display font-bold text-4xl md:text-6xl lg:text-7xl leading-[0.95] text-white max-w-3xl">
                  Ready to audio-mine <br /> the world?
                </h2>
                <p className="text-white/85 text-lg max-w-xl">
                  Whether you make the music or live for it — this is where attention becomes joy, and joy becomes value.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/contact">
                    <Button data-testid="cta-final-get-started" size="lg" className="rounded-full h-14 px-10 bg-white text-primary hover:bg-white/90 font-semibold text-base shadow-[0_16px_40px_-8px_rgba(0,0,0,0.35)]">
                      Get Started <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/why-audiomine">
                    <Button size="lg" variant="outline" className="rounded-full h-14 px-10 border-white/40 bg-white/10 hover:bg-white/20 text-white font-semibold text-base backdrop-blur">
                      Why AudioMine?
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
